import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  BaseChart,
  Box,
  EMPTY_ARRAY,
  Flex,
  FormControlSizes,
  FormGroup,
  HStack,
  Input,
  logger,
  MixpanelEvent,
  SideEnum,
  Slider,
  Text,
  toBigWithDefault,
  useDynamicCallback,
  useMixpanel,
} from '@talos/kyoko';
import Big from 'big.js';
import { differenceInDays } from 'date-fns';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { HeaderSideSelector } from '../OMS/HeaderSideSelector';
import { CalendarSpread, type OptionStrategy } from '../OMS/NewOrder/models';
import {
  selectIsOptionPayoffAvailable,
  selectOptionStrategy,
  selectOptionStrategyAsRows,
  selectResolvedSecurities,
} from './MultilegComboSlice';
import type { OptionStrategyRow } from './types';
import type { BSVariables } from './utils/BlackScholes';
import { calculateY, getSampleIncrement, mapSampleDataToPlotData, type Point } from './utils/OptionPayoffDataFactory';

export const OptionPayout = memo(({ panelID }: { panelID: string }) => {
  const theme = useTheme();
  const mixpanel = useMixpanel();

  const rowData: OptionStrategyRow[] = useAppStateSelector(state => selectOptionStrategyAsRows(state, panelID));
  const isChartable = useAppStateSelector(state => selectIsOptionPayoffAvailable(state, panelID));
  const optionStrategy: OptionStrategy | undefined = useAppStateSelector(state => selectOptionStrategy(state, panelID));
  const securities = useAppStateSelector(state => selectResolvedSecurities(state, panelID));

  const [direction, setDirection] = useState<SideEnum>(SideEnum.Buy);
  const [data, setData] = useState<Point[]>([]);
  const [expiryData, setExpiryData] = useState<Point[]>([]);
  const [breakevenPoints, setBreakevenPoints] = useState<number[]>([]);
  const [startPrice, setStartPrice] = useState<number>(0);
  const [endPrice, setEndPrice] = useState<number>(0);
  const [daysToExpiry, setDaysToExpiry] = useState<number>(-1);
  const [iv, setIV] = useState<number>(10);
  const isCalendarSpread = useMemo(() => optionStrategy instanceof CalendarSpread, [optionStrategy]);
  const [initialisedSymbols, setInitialisedSymbols] = useState<string | undefined>();

  // we want to track on mixpanel ONLY the first time a value is customized for a symbol
  const [trackedSymbols, setTrackedSymbols] = useState<string | undefined>();

  const nearLegExpiryDayCount = useMemo(() => {
    if (!isChartable) {
      return 0;
    }
    const firstToExpire = rowData.reduce((acc: Date | null, row) => {
      const expiry = new Date(row.expiryDate!.value);
      if (!acc) {
        return expiry;
      }
      return expiry < acc ? expiry : acc;
    }, null);
    return differenceInDays(firstToExpire || new Date(), new Date());
  }, [rowData, isChartable]);

  const farLegExpiryDayCount = useMemo(() => {
    if (!isChartable) {
      return 0;
    }
    const lastToExpire = rowData.reduce((acc: Date | null, row) => {
      const expiry = new Date(row.expiryDate!.value);
      if (!acc) {
        return expiry;
      }
      return expiry > acc ? expiry : acc;
    }, null);
    return differenceInDays(lastToExpire || new Date(), new Date());
  }, [rowData, isChartable]);

  const underlyingPrice = useMemo(() => toBigWithDefault(rowData?.[0]?.underlyingPrice, 0).toNumber(), [rowData]);
  const legSecurities = useMemo(() => securities.map(s => s?.Symbol).join(','), [securities]);

  useEffect(() => {
    if (isChartable && !initialisedSymbols) {
      setInitialisedSymbols(legSecurities);
    }
  }, [isChartable, legSecurities, initialisedSymbols]);

  useEffect(() => {
    // important that we check they are defined as well as not equal, not just not equal
    if (initialisedSymbols && legSecurities && legSecurities !== initialisedSymbols) {
      setInitialisedSymbols(undefined);
      setTrackedSymbols(undefined);
    }
  }, [initialisedSymbols, legSecurities]);

  useEffect(() => {
    if (!isChartable && !initialisedSymbols) {
      setData(EMPTY_ARRAY);
      setExpiryData(EMPTY_ARRAY);
      setBreakevenPoints(EMPTY_ARRAY);
      setStartPrice(0);
      setEndPrice(0);
      setDaysToExpiry(-1);
      return;
    }
    if (!isChartable && initialisedSymbols) {
      // if we reach here it means that we have previously rendered the chart, but now we lost some market data that is 'no longer chartable'
      // however we can still continue showing the chart using stale data rather than blanking it which causes flickers if the market data
      // flickers in and out due to liquidity.
      return;
    }

    const tempData: Point[] = [];
    const tempExpiryData: Point[] = [];
    const tempBreakevenPoints: number[] = [];

    if (!endPrice && underlyingPrice) {
      // add extra padding on both sides so the chart doesn't look so zoomed in
      setStartPrice(Math.floor(underlyingPrice * 0.7));
      setEndPrice(Math.floor(underlyingPrice * 1.3));
      return;
    }

    if (daysToExpiry === -1 || daysToExpiry > nearLegExpiryDayCount) {
      setDaysToExpiry(nearLegExpiryDayCount);
    }
    const increment = getSampleIncrement(startPrice, endPrice);

    const oneOffCost = rowData
      .reduce((acc: Big, row) => {
        const ratio = toBigWithDefault(row.ratio, 1);
        if (ratio.gt(0)) {
          const dollarAmount = toBigWithDefault(row.underlyingPrice, 0).mul(toBigWithDefault(row.offer, 0).mul(ratio));
          return acc.minus(dollarAmount);
        }
        const dollarAmount = toBigWithDefault(row.underlyingPrice, 0).mul(
          toBigWithDefault(row.bid, 0).mul(ratio.abs())
        );
        return acc.add(dollarAmount);
      }, new Big(0))
      .mul(direction === SideEnum.Buy ? 1 : -1)
      .toNumber();

    let previous = 0;

    for (let i = startPrice; i < endPrice; i += increment) {
      const baseVariables: Omit<BSVariables, 'strike'> = {
        stock: i,
        term: daysToExpiry / 365,
        vola: iv / 100,
        interest: 0,
      };
      const baseVariablesAtExpiry: Omit<BSVariables, 'strike'> = {
        stock: i,
        term: 0,
        vola: iv / 100,
        interest: 0,
      };

      const legExpiryDiff = isCalendarSpread ? farLegExpiryDayCount - nearLegExpiryDayCount : undefined;
      const y = calculateY(rowData, baseVariables, oneOffCost, direction, legExpiryDiff);
      const y2 = calculateY(rowData, baseVariablesAtExpiry, oneOffCost, direction, legExpiryDiff);

      if ((previous < 0 && y > 0) || (y < 0 && previous > 0)) {
        tempBreakevenPoints.push(i);
      }

      previous = y;
      tempData.push([i, y]);
      tempExpiryData.push([i, y2]);
    }

    setData(tempData);
    setExpiryData(tempExpiryData);
    setBreakevenPoints(tempBreakevenPoints);

    // Purposely not updating chart when rowData changes because otherwise with ticking prices the chart constantly recomputes and jitters around due to the small movements
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isChartable,
    initialisedSymbols,
    daysToExpiry,
    endPrice,
    startPrice,
    iv,
    direction,
    nearLegExpiryDayCount,
    farLegExpiryDayCount,
    isCalendarSpread,
  ]);

  const chartOptions = useMemo(() => {
    const breakevenLines = breakevenPoints.map(i => {
      return {
        color: theme.colors.orange.lighten,
        dashStyle: 'Dash',
        value: i,
        width: 1,
      };
    });

    return {
      title: {
        text: !isChartable && initialisedSymbols ? 'Chart data stale due to underlying rates being unavailable' : '',
        align: 'center',
        style: {
          color: theme.colorTextWarning,
          font: `normal ${theme.fontSizeDefault}rem ${theme.fontFamily}`,
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          zones: [
            {
              value: 0,
              className: 'zone-negative',
            },
            {
              className: 'zone-positive',
            },
          ],
        },
      },
      xAxis: {
        plotLines: breakevenLines,
      },
      tooltip: {
        shared: true,
        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} USD</b><br/>',
      },
      series: [
        {
          data: mapSampleDataToPlotData(data),
          type: 'area',
          name: 'P&L',
          lineWidth: 2,
        },
        {
          data: mapSampleDataToPlotData(expiryData),
          marker: {
            enabled: false, // Set this to false to hide the circles on data points
          },
          type: 'spline',
          name: '@Expiry',
          color: theme.colors.yellow.lighten,
          zones: [],
          lineWidth: 1,
          dashStyle: 'LongDash',
        },
      ],
    };
  }, [data, expiryData, breakevenPoints, theme, isChartable, initialisedSymbols]);

  const trackMixpanel = useDynamicCallback(() => {
    if (!trackedSymbols) {
      setTrackedSymbols(legSecurities);
      mixpanel.track(MixpanelEvent.ChangePayoutChartValues);
    }
  });

  if (!isChartable && !initialisedSymbols) {
    return null;
  }

  return (
    <>
      <Box
        background="backgroundContent"
        flex="1 1 auto"
        p="spacing12"
        overflow="hidden"
        data-testid="option-payout-wrapper"
        id="multileg-deepdive-option-payout"
        borderTop="solid 1px"
        borderColor="backgroundBody"
      >
        <Flex gap="spacing24">
          <FormGroup label="Direction" minWidth="160px" gap="spacing4">
            <HeaderSideSelector
              size={FormControlSizes.Small}
              showTwoWay={false}
              disabled={!isChartable}
              onChange={d => {
                trackMixpanel();
                setDirection(d);
              }}
              side={direction}
            />
          </FormGroup>
          <FormGroup label="IV" gap="spacing4" w="100%">
            <Slider
              useRange={false}
              value={iv}
              max={100}
              disabled={!isChartable}
              onChange={value => {
                trackMixpanel();
                setIV(value as number);
              }}
            />
            <HStack justifyContent="space-between" w="100%">
              <Text size="fontSizeTiny" color="colorTextSubtle">
                0%
              </Text>
              <Input
                width="100px"
                disabled={!isChartable}
                value={iv}
                onChange={e => {
                  trackMixpanel();
                  setIV(toBigWithDefault(e.target.value, 0).toNumber());
                }}
                size={FormControlSizes.Tiny}
                suffix="%"
              />
              <Text size="fontSizeTiny" color="colorTextSubtle">
                100%
              </Text>
            </HStack>
          </FormGroup>

          <FormGroup label="Days until Expiry" alignItems="start" w="100%" gap="spacing4">
            <Slider
              useRange={false}
              value={daysToExpiry}
              max={nearLegExpiryDayCount}
              disabled={!isChartable}
              reverse={true}
              onChange={value => {
                trackMixpanel();
                setDaysToExpiry(value as number);
              }}
            />

            <HStack justifyContent="space-between" w="100%">
              <Text size="fontSizeTiny" color="colorTextSubtle">
                Today
              </Text>
              <Input
                width="100px"
                value={daysToExpiry}
                disabled={!isChartable}
                onChange={e => {
                  trackMixpanel();
                  setDaysToExpiry(toBigWithDefault(e.target.value, 0).toNumber());
                }}
                size={FormControlSizes.Tiny}
                suffix={daysToExpiry > 1 ? 'Days' : 'Day'}
              />
              <Text size="fontSizeTiny" color="colorTextSubtle">
                Expiry Date
              </Text>
            </HStack>
          </FormGroup>
          <FormGroup label="Underlying Price (Chart Range)" alignItems="start" w="100%" gap="spacing4">
            <Slider
              useRange={true}
              value={[startPrice, endPrice]}
              min={0}
              disabled={!isChartable}
              max={Math.floor(toBigWithDefault(underlyingPrice, 0).mul(2).toNumber())}
              onChange={value => {
                if (typeof value === 'number') {
                  logger.warn(`Slider value should always be an array, but got the value: ${value}`);
                  return;
                }
                trackMixpanel();
                if (Array.isArray(value)) {
                  setStartPrice(value[0]);
                  setEndPrice(value[1]);
                }
              }}
            />
            <HStack justifyContent="space-between" w="100%">
              <Input
                width="100px"
                value={startPrice}
                onChange={e => {
                  trackMixpanel();
                  setStartPrice(toBigWithDefault(e.target.value, 0).toNumber());
                }}
                size={FormControlSizes.Tiny}
                disabled={!isChartable}
                suffix={rowData?.[0]?.underlyingQuoteCurrency}
              />
              <Input
                width="100px"
                value={endPrice}
                onChange={e => {
                  trackMixpanel();
                  setEndPrice(toBigWithDefault(e?.target.value, 0).toNumber());
                }}
                size={FormControlSizes.Tiny}
                disabled={!isChartable}
                suffix={rowData?.[0]?.underlyingQuoteCurrency}
              />
            </HStack>
          </FormGroup>
        </Flex>
        <BaseChart options={chartOptions} />
      </Box>
    </>
  );
});
